import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

function AuthShell({ title, subtitle, children, footer }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-slate-900 text-white p-12">
        <Link to="/" className="flex items-center gap-2" data-testid="auth-brand-link">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center font-display font-extrabold">E</div>
          <div className="font-display font-extrabold">English Ready</div>
        </Link>
        <div>
          <span className="rle-eyebrow text-emerald-400">Real English • Real Situations</span>
          <h2 className="mt-3 font-display font-extrabold text-4xl leading-tight">Practise the English you need for real life.</h2>
          <p className="mt-4 text-slate-300">Australia · Canada · UK</p>
        </div>
        <p className="text-xs text-slate-400">Educational language-learning and communication-practice platform. Not immigration, legal, medical or financial advice.</p>
      </div>
      <div className="flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden font-display font-extrabold text-slate-900" data-testid="auth-brand-link-mobile">English Ready</Link>
          <h1 className="mt-4 font-display font-extrabold text-3xl text-slate-900">{title}</h1>
          {subtitle && <p className="mt-2 text-slate-600">{subtitle}</p>}
          <div className="mt-8">{children}</div>
          {footer && <div className="mt-6 text-sm text-slate-600 text-center">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const { login } = useAuth(); const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      if (u.role === "admin") nav("/admin");
      else if (!u.onboarding_completed) nav("/onboarding");
      else nav("/dashboard");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Welcome back" subtitle="Log in to continue your practice."
      footer={<>Don't have an account? <Link to="/register" className="text-emerald-700 font-semibold" data-testid="login-to-register-link">Sign up free</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input"/></div>
        <div><Label>Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input"/></div>
        {err && <div className="text-sm text-red-600" data-testid="login-error">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 h-11" data-testid="login-submit-btn">{busy ? "Signing in…" : "Log in"}</Button>
        <div className="text-center"><Link to="/forgot-password" className="text-sm text-slate-600 hover:text-slate-900" data-testid="login-forgot-link">Forgot password?</Link></div>
      </form>
    </AuthShell>
  );
}

export function Register() {
  const [email, setEmail] = useState(""); const [password, setPassword] = useState(""); const [name, setName] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false); const [verifyToken, setVerifyToken] = useState("");
  const { register } = useAuth(); const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const data = await register(email, password, name);
      setVerifyToken(data.verification_token || "");
      toast.success(data.email_sent ? "Account created! We've emailed you a verification link." : "Account created!");
      setTimeout(() => nav("/onboarding"), data.email_sent ? 1800 : 1200);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Start learning free" subtitle="No credit card required."
      footer={<>Already have an account? <Link to="/login" className="text-emerald-700 font-semibold" data-testid="register-to-login-link">Log in</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Your name (optional)</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input"/></div>
        <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="register-email-input"/></div>
        <div><Label>Password (min 8 characters)</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="register-password-input"/></div>
        {err && <div className="text-sm text-red-600" data-testid="register-error">{err}</div>}
        {verifyToken && (
          <div className="text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-xl p-3" data-testid="register-verify-token-note">
            <div className="font-semibold mb-1">Email verification token</div>
            <div className="font-mono break-all">{verifyToken}</div>
            <div className="mt-1">We couldn't email you right now — you can verify with this token on your profile page.</div>
          </div>
        )}
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 h-11" data-testid="register-submit-btn">{busy ? "Creating…" : "Create account"}</Button>
      </form>
    </AuthShell>
  );
}

export function ForgotPassword() {
  const [email, setEmail] = useState(""); const [sent, setSent] = useState(false); const [token, setToken] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true); if (data.reset_token) setToken(data.reset_token);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Reset your password" subtitle="Enter your email and we'll send you a reset link."
      footer={<Link to="/login" className="text-emerald-700 font-semibold" data-testid="fp-back-to-login">Back to log in</Link>}>
      {sent ? (
        <div className="rle-card p-4" data-testid="forgot-sent">
          <div className="font-semibold text-slate-900">If an account exists for this email, we've sent a password reset link.</div>
          <div className="mt-1 text-sm text-slate-600">Check your inbox (and spam folder). The link is valid for 1 hour.</div>
          {token && (
            <>
              <div className="mt-2 text-xs text-slate-500">Email delivery is unavailable right now — use this reset token instead:</div>
              <div className="mt-1 p-2 bg-slate-50 rounded font-mono text-xs break-all" data-testid="forgot-token">{token}</div>
              <Link to={`/reset-password?token=${token}`}><Button className="mt-3 w-full rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="forgot-use-token-btn">Use token now</Button></Link>
            </>
          )}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input"/></div>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 h-11" data-testid="forgot-submit-btn">{busy ? "Sending…" : "Send reset link"}</Button>
        </form>
      )}
    </AuthShell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || ""); const [password, setPassword] = useState("");
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password reset. Please log in.");
      nav("/login");
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Set a new password">
      <form onSubmit={submit} className="space-y-4">
        <div><Label>Reset token</Label><Input value={token} onChange={(e) => setToken(e.target.value)} required data-testid="reset-token-input"/></div>
        <div><Label>New password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="reset-password-input"/></div>
        {err && <div className="text-sm text-red-600">{err}</div>}
        <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 h-11" data-testid="reset-submit-btn">{busy ? "Saving…" : "Reset password"}</Button>
      </form>
    </AuthShell>
  );
}

export function VerifyEmail() {
  const [params] = useSearchParams();
  const [token, setToken] = useState(params.get("token") || ""); const [done, setDone] = useState(false);
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await api.post("/auth/verify-email", { token }); setDone(true); toast.success("Email verified!"); }
    catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  return (
    <AuthShell title="Verify your email">
      {done ? <div className="rle-card p-4">Your email is verified. <Link to="/dashboard" className="text-emerald-700 font-semibold">Go to dashboard</Link>.</div> : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>Verification token</Label><Input value={token} onChange={(e) => setToken(e.target.value)} required data-testid="verify-token-input"/></div>
          {err && <div className="text-sm text-red-600">{err}</div>}
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-emerald-700 hover:bg-emerald-800 h-11" data-testid="verify-submit-btn">{busy ? "Verifying…" : "Verify email"}</Button>
        </form>
      )}
    </AuthShell>
  );
}
